import './OrgContact.scss';

import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalTheme } from '../contexts/ThemeContext';
import { useConfirm } from 'material-ui-confirm';
import ReactFlow, { useNodesState, useEdgesState, addEdge, updateEdge, MarkerType, Background, Controls, ControlButton, MiniMap, BaseEdge, EdgeLabelRenderer, getSmoothStepPath, useReactFlow, ReactFlowProvider } from 'reactflow';
import dagre from '@dagrejs/dagre';
import { gql, useQuery, useMutation } from '@apollo/client';
import 'reactflow/dist/style.css';
import { Container, Typography } from '@mui/material';
import Page from '../components/Page';
import Iconify from '../components/Iconify';

const GET_APP_DATA = gql`
  query {
    orgContactGroups { id name parentId groupAddress users { id name } subGroups { id } }
    orgContactUsers { id name userAddress groups { id name } }
    orgContactConfig { theme }
  }
`;
const CREATE_GROUP = gql`mutation CreateGroup($name: String!) { createOrgContactGroup(name: $name, parentId: null, groupAddress: "") { id name } }`;
const UPDATE_PATH = gql`mutation UpdatePath($id: ID!, $parentId: ID) { updateOrgContactPath(id: $id, parentId: $parentId) { id } }`;
const UPDATE_GROUP = gql`mutation UpdateGroup($id: ID!, $name: String!) { updateOrgContactGroup(id: $id, name: $name) { id name } }`;
const TOGGLE_USER_GROUP = gql`mutation ToggleUserGroup($userDocId: ID!, $groupDocId: ID!) { toggleOrgContactUserGroup(userDocId: $userDocId, groupDocId: $groupDocId) { id } }`;
const DELETE_GROUP = gql`mutation DeleteGroup($id: ID!) { deleteOrgContactGroup(id: $id) { id } }`;

const itemsPerPage = 5; // 每頁顯示數量

// 可折疊區塊元件
const CollapsibleSection = ({ title, children, id, isOpen, onToggle }) => (
  <details id={id} className="collapsible-section" open={isOpen}>
    <summary onClick={(e) => { e.preventDefault(); onToggle(); }}>{title}</summary>
    <div>{children}</div>
  </details>
);

// 遞迴元件：顯示樹狀結構 (群組 -> 子群組 & 使用者)
const GroupTree = ({ groups, parentId = null, level = 0, onRemoveUser, onDeleteGroup }) => {
  const { t } = useTranslation();
  const children = groups.filter(g => g.parentId === parentId);
  if (children.length === 0 && !parentId) return null; // 避免顯示無效根節點

  return (
    <div style={{ paddingLeft: level ? 15 : 0 }}>
      {children.map(g => (
        <details key={g.id} open style={{ marginBottom: 5 }}>
          <summary style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', lineHeight: '1.4', fontSize: '0.9rem' }} className="text-base">
            <span style={{ marginRight: '4px' }}>📂</span>
            <span style={{ wordBreak: 'break-word', flex: 1 }}>
              {g.name} {g.users?.length > 0 ? `(${g.users.length})` : ''}
            </span>
            <span 
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDeleteGroup && onDeleteGroup(g.id); }} 
              style={{ cursor: 'pointer', color: '#ff4d4f', marginLeft: 8, display: 'flex', alignItems: 'center' }} 
              title={t('common.delete')}
            >
              <Iconify icon="eva:trash-2-outline" width={16} height={16} />
            </span>
          </summary>
          <div style={{ marginLeft: 15, borderLeft: '1px solid #ddd', paddingLeft: 5 }}>
            {g.users.map(u => (
              <div key={u.id} style={{ fontSize: '0.9rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '2px 0' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', marginRight: 5 }}>
                  <span style={{ marginRight: '4px' }}>👤</span>
                  <span style={{ wordBreak: 'break-word', lineHeight: '1.4' }}>{u.name}</span>
                </div>
                <span onClick={() => onRemoveUser && onRemoveUser(u.id, g.id)} style={{ cursor: 'pointer', color: '#ff4d4f', flexShrink: 0 }} title={t('org.remove_confirm')}>×</span>
              </div>
            ))}
            <GroupTree groups={groups} parentId={g.id} level={level + 1} onRemoveUser={onRemoveUser} onDeleteGroup={onDeleteGroup} />
          </div>
        </details>
      ))}
    </div>
  );
};

// 自訂連線元件：支援選取變色與刪除按鈕
const CustomEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  selected,
}) => {
  const { t } = useTranslation();
  const { deleteElements } = useReactFlow();
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const onEdgeClick = (evt) => {
    evt.stopPropagation();
    deleteElements({ edges: [{ id }] });
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={selected ? 'url(#red-arrow)' : markerEnd} style={{
          ...style,
          stroke: selected ? '#ff4d4f' : style.stroke,
          strokeWidth: selected ? 3 : style.strokeWidth,
        }} />
      {selected && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              fontSize: 12,
              pointerEvents: 'all',
            }}
            className="nodrag nopan"
          >
            <button
              style={{ width: '20px', height: '20px', background: '#ff4d4f', color: '#fff', border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '12px', padding: 0, boxShadow: '0 0 4px rgba(0,0,0,0.3)' }}
              onClick={onEdgeClick}
              title={t('org.remove_edge')}
            >
              ×
            </button>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const edgeTypes = { custom: CustomEdge };
const proOptions = { hideAttribution: true };

// 自訂縮放工具列 (包含排列按鈕)
const CustomControls = ({ onLayout }) => {
  const { t } = useTranslation();
  const { zoomIn, zoomOut, fitView } = useReactFlow();

  return (
    <Controls showZoom={false} showFitView={false} showInteractive={false}>
      <ControlButton onClick={() => onLayout('TB')} title={t('org.layout_tb')}>
        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none"><rect x="7" y="2" width="10" height="6" rx="1"/><rect x="7" y="16" width="10" height="6" rx="1"/><line x1="12" y1="8" x2="12" y2="16"/></svg>
      </ControlButton>
      <ControlButton onClick={() => onLayout('LR')} title={t('org.layout_lr')}>
        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none"><rect x="2" y="7" width="6" height="10" rx="1"/><rect x="16" y="7" width="6" height="10" rx="1"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
      </ControlButton>
      <ControlButton onClick={() => zoomIn()} title={t('org.zoom_in')}>
        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
      </ControlButton>
      <ControlButton onClick={() => zoomOut()} title={t('org.zoom_out')}>
        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
      </ControlButton>
      <ControlButton onClick={() => fitView({ padding: 0.2 })} title={t('org.fit_view')}>
        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>
      </ControlButton>
    </Controls>
  );
};

const OrgContactFlow = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { loading, data, refetch } = useQuery(GET_APP_DATA, { fetchPolicy: 'network-only' });
  const [createGroup] = useMutation(CREATE_GROUP);
  const [updatePath] = useMutation(UPDATE_PATH);
  const [updateGroup] = useMutation(UPDATE_GROUP);
  const [toggleUserGroup] = useMutation(TOGGLE_USER_GROUP);
  const [deleteGroup] = useMutation(DELETE_GROUP);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { fitView, setCenter } = useReactFlow();

  const edgeUpdateSuccessful = useRef(true);

  // 主題狀態
  const { globalTheme } = useGlobalTheme();
  const [highlightedId, setHighlightedId] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [openSection, setOpenSection] = useState(null); // 預設全部摺疊
  const hideTimer = useRef(null);
  const hasHovered = useRef(false);
  
  // 人員搜尋狀態
  const [searchTerm, setSearchTerm] = useState("");
  const [currentUserPage, setCurrentUserPage] = useState(1);
  const filteredUsers = data?.orgContactUsers?.filter(u => u.name.toLowerCase().includes(searchTerm.toLowerCase())) || [];
  const totalUserPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const displayedUsers = filteredUsers.slice((currentUserPage - 1) * itemsPerPage, currentUserPage * itemsPerPage);

  // 部門搜尋狀態
  const [searchGroupTerm, setSearchGroupTerm] = useState("");
  const [currentGroupPage, setCurrentGroupPage] = useState(1);
  const filteredGroups = data?.orgContactGroups?.filter(g => g.name.toLowerCase().includes(searchGroupTerm.toLowerCase())) || [];
  const totalGroupPages = Math.ceil(filteredGroups.length / itemsPerPage);
  const displayedGroups = filteredGroups.slice((currentGroupPage - 1) * itemsPerPage, currentGroupPage * itemsPerPage);

  // 當搜尋關鍵字改變時，重置回第一頁
  useEffect(() => { setCurrentUserPage(1); }, [searchTerm]);
  useEffect(() => { setCurrentGroupPage(1); }, [searchGroupTerm]);

  // 自動修正頁碼：如果當前頁碼超過總頁數（例如刪除最後一項後），自動跳回最後一頁
  useEffect(() => {
    if (currentGroupPage > totalGroupPages && totalGroupPages > 0) {
      setCurrentGroupPage(totalGroupPages);
    }
  }, [totalGroupPages, currentGroupPage]);

  useEffect(() => {
    if (currentUserPage > totalUserPages && totalUserPages > 0) {
      setCurrentUserPage(totalUserPages);
    }
  }, [totalUserPages, currentUserPage]);

  const startHideTimer = useCallback(() => {
    if (hasHovered.current) return;
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
        setSidebarVisible(false);
    }, 3600);
  }, []);

  const cancelHideTimer = () => {
      hasHovered.current = true;
      clearTimeout(hideTimer.current);
  };

  useEffect(() => {
      startHideTimer();
      return () => clearTimeout(hideTimer.current); // Cleanup on unmount
  }, [startHideTimer]);


  useEffect(() => {
    if (data?.orgContactGroups) {
      if (data.orgContactGroups.length === 0) {
        setNodes([]);
        setEdges([]);
        return;
      }
      const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
      g.setGraph({ rankdir: 'TB' });

      const newEdges = data.orgContactGroups.filter(g => g.parentId).map(g => ({
        id: `e${g.parentId}-${g.id}`, source: g.parentId, target: g.id, type: 'custom',
        style: { strokeWidth: 1, stroke: 'var(--edge)' }, 
        markerEnd: { type: MarkerType.Arrow, width: 25, height: 25, color: 'var(--edge)' } 
      }));

      // 設定節點大小供 dagre 計算佈局 (寬度加大以容納長名稱)
      data.orgContactGroups.forEach(group => g.setNode(group.id, { width: 180, height: 60 }));
      newEdges.forEach(e => g.setEdge(e.source, e.target));

      dagre.layout(g);

      const newNodes = data.orgContactGroups.map(group => {
        const nodeWithPos = g.node(group.id);
        return {
          id: group.id,
          className: 'org-react-node',
          data: { 
            label: (
              <div className="node-label">
                {group.name} {group.users?.length > 0 ? `(${group.users.length})` : ''}
              </div>
            ) 
          },
          position: { x: nodeWithPos.x - 90, y: nodeWithPos.y - 30 }
        };
      });

      setNodes(newNodes);
      setEdges(newEdges);
      setTimeout(() => {
        fitView({ padding: 0.2 });
      }, 100);
    }
  }, [data, globalTheme, fitView]); // 依賴 theme key 更新 marker顏色

    const onLayout = (direction) => {
    const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: direction });
    nodes.forEach(n => g.setNode(n.id, { width: 180, height: 60 }));
    edges.forEach(e => g.setEdge(e.source, e.target));
    dagre.layout(g);
    setNodes(nodes.map(n => ({ ...n, position: { x: g.node(n.id).x - 90, y: g.node(n.id).y - 30 } })));
  };


  const handleAdd = async () => {
    const name = prompt(t('org.dept_name_prompt'));
    if (name) { await createGroup({ variables: { name } }); refetch(); }
  };

  const handleRenameGroup = async (id, currentName) => {
    const name = prompt(t('org.rename_dept_prompt'), currentName);
    if (name && name !== currentName) {
      await updateGroup({ variables: { id, name } });
      refetch();
    }
  };

  const handleDeleteGroup = (groupId) => {
    confirm({
      title: t('org.delete_dept_confirm_title'),
      description: t('org.delete_dept_confirm_desc'),
      confirmationText: t('common.delete'),
      cancellationText: t('common.cancel'),
      confirmationButtonProps: { color: 'error' }
    }).then(async () => {
      const idsToDelete = [groupId];
      const collectSubGroups = (parentId) => {
        const children = data?.orgContactGroups?.filter(g => g.parentId === parentId) || [];
        for (const child of children) {
          idsToDelete.push(child.id);
          collectSubGroups(child.id);
        }
      };
      collectSubGroups(groupId);
      idsToDelete.reverse(); // 重要：由子群組刪起，避免父群組刪除後引起的外鍵錯誤

      for (const id of idsToDelete) {
        try { await deleteGroup({ variables: { id } }); } catch (e) { console.error(e); }
      }
      await refetch();
    }).catch(() => {});
  };

  const handleToggleGroup = async (userDocId, groupDocId) => {
    await toggleUserGroup({ variables: { userDocId, groupDocId } });
    await refetch();
  };

  const handleUpdateParent = async (groupId, newParentId) => {
    try {
      await updatePath({ variables: { id: groupId, parentId: newParentId || null } });
    } catch (e) {
      confirm({
        title: t('common.warning'),
        description: e.message || 'Circular reference detected.',
        confirmationText: t('common.ok'),
        hideCancelButton: true,
      });
    }
    await refetch();
  };

  const onConnect = async (params) => {
    try {
      setEdges((eds) => addEdge({ ...params, type: 'custom', 
        style: { strokeWidth: 1, stroke: 'var(--edge)' }, 
        markerEnd: { type: MarkerType.Arrow, width: 25, height: 25, color: 'var(--edge)' }
      }, eds));
      await updatePath({ variables: { id: params.target, parentId: params.source } });
    } catch (e) {
      confirm({
        title: t('common.warning'),
        description: e.message || 'Circular reference detected.',
        confirmationText: t('common.ok'),
        hideCancelButton: true,
      });
    }
    await refetch();
  };

  const onEdgeUpdateStart = useCallback(() => {
    edgeUpdateSuccessful.current = false;
  }, []);

  const onEdgeUpdate = useCallback(async (oldEdge, newConnection) => {
    try {
      edgeUpdateSuccessful.current = true;
      setEdges((els) => updateEdge(oldEdge, newConnection, els));
      await updatePath({ variables: { id: newConnection.target, parentId: newConnection.source } });
    } catch (e) {
      confirm({
        title: t('common.warning'),
        description: e.message || 'Circular reference detected.',
        confirmationText: t('common.ok'),
        hideCancelButton: true,
      });
    }
    await refetch();
  }, [updatePath, confirm, t, refetch]);

  const onEdgeUpdateEnd = useCallback(async (_, edge) => {
    if (!edgeUpdateSuccessful.current) {
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
      await updatePath({ variables: { id: edge.target, parentId: null } });
      await refetch();
    }
    edgeUpdateSuccessful.current = true;
  }, [updatePath, refetch]);

  const onEdgesDelete = useCallback(async (deletedEdges) => {
    for (const edge of deletedEdges) {
      await updatePath({ variables: { id: edge.target, parentId: null } });
    }
    await refetch();
  }, [updatePath, refetch]);

  const onNodesDelete = useCallback(async (deletedNodes) => {
    for (const node of deletedNodes) {
      handleDeleteGroup(node.id); // 此函數內部已有 confirm 與遞迴 logic
    }
  }, [handleDeleteGroup]);

  const onNodeClick = useCallback((_, node) => {
    // 自動展開部門管理區塊
    setOpenSection('dept');

    // 計算該部門在第幾頁並切換
    if (data?.orgContactGroups) {
      const currentFiltered = data.orgContactGroups.filter(g => g.name.toLowerCase().includes(searchGroupTerm.toLowerCase()));
      const index = currentFiltered.findIndex(g => g.id === node.id);
      if (index !== -1) {
        const targetPage = Math.floor(index / itemsPerPage) + 1;
        if (targetPage !== currentGroupPage) {
          setCurrentGroupPage(targetPage);
        }
        // 延遲捲動以等待渲染
        setTimeout(() => {
          const element = document.getElementById(`group-section-${node.id}`);
          if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }

    setHighlightedId(node.id); // 設定高亮 ID
    setTimeout(() => setHighlightedId(null), 2000); // 2秒後取消高亮
  }, [data?.orgContactGroups, searchGroupTerm, currentGroupPage]);

  const onMiniMapNodeClick = useCallback((event, node) => {
    const x = node.position.x + 90; // Center X (node width is 180)
    const y = node.position.y + 30; // Center Y (node height is 60)
    setCenter(x, y, { zoom: 1.2, duration: 800 });
    onNodeClick(event, node); // Keep the side panel linkage & highlight effect
  }, [setCenter, onNodeClick]);

  return (
    <Page title={t('nav.contact')}>
      <Container maxWidth={false} disableGutters sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column', pb: 2, pt: 0 }}>
        <Typography variant="h4" sx={{ mb: 1, px: 3 }}>
          {t('nav.contact')}
        </Typography>

        {/* 覆蓋掉 SCSS 中寫死的側邊欄寬度與位移，確保縮小後切換功能依然正常 */}
        <style>{`
          /* 美化組織樹與列表的捲軸 (解決 Windows 預設捲軸過粗的問題) */
          .org-contact-layout .sidebar *::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          .org-contact-layout .sidebar *::-webkit-scrollbar-track {
            background: transparent;
          }
          .org-contact-layout .sidebar *::-webkit-scrollbar-thumb {
            background: rgba(150, 150, 150, 0.4);
            border-radius: 4px;
          }
          .org-contact-layout .sidebar *::-webkit-scrollbar-thumb:hover {
            background: rgba(150, 150, 150, 0.6);
          }

          .org-contact-layout .sidebar-wrapper {
            width: 300px !important; /* 預設寬度 */
            max-width: calc(100vw - 50px) !important; /* 避免在小尺寸螢幕下撐爆畫面，預留 50px 給按鈕 */
            position: absolute !important;
            right: 0 !important;
            left: auto !important;
            top: 0 !important;
            bottom: 0 !important;
            z-index: 10;
          }
          .org-contact-layout .sidebar-hidden {
            transform: translateX(calc(100% - 32px)) !important; /* 保留 32px 讓按鈕可見 */
          }

          /* 確保 Toggle 按鈕在視窗變小或在行動裝置檢視時依然顯示 */
          .org-contact-layout .sidebar-toggle-tab {
            display: flex !important;
            visibility: visible !important;
          }
        `}</style>

        <div className="org-contact-layout" style={{ flex: 1, position: 'relative' }}>
          {/* 定義紅色箭頭的 SVG Marker (供選取時使用) */}
          <svg style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }}>
            <defs>
              <marker id="red-arrow" viewBox="0 0 12.5 12.5" refX="12.5" refY="6.25" markerWidth="12.5" markerHeight="12.5" orient="auto-start-reverse" markerUnits="userSpaceOnUse">
                <polyline points="0 0 12.5 6.25 0 12.5" fill="none" stroke="#ff4d4f" strokeWidth="3" />
              </marker>
            </defs>
          </svg>
    
          <div style={{ flex: 1, position: 'relative' }}>
            <ReactFlow 
          nodes={nodes} 
          edges={edges} 
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange} 
          onEdgesChange={onEdgesChange} 
          onConnect={onConnect} 
          onEdgeUpdate={onEdgeUpdate}
          onEdgeUpdateStart={onEdgeUpdateStart}
          onEdgeUpdateEnd={onEdgeUpdateEnd}
          onEdgesDelete={onEdgesDelete}
          onNodesDelete={onNodesDelete}
          deleteKeyCode={['Backspace', 'Delete']}
          onNodeClick={onNodeClick} 
          proOptions={proOptions}
        >
          <Background /><CustomControls onLayout={onLayout} />
          <MiniMap 
            position="top-left" 
            ariaLabel={null} 
            nodeColor={(n) => n.id === highlightedId ? 'rgba(255, 77, 79, 0.5)' : 'var(--node-bg)'} 
            nodeStrokeColor={(n) => n.id === highlightedId ? 'rgba(255, 77, 79, 0.5)' : 'var(--node-border)'} 
            nodeStrokeWidth={3} 
            nodeBorderRadius={5} 
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }} 
            maskColor="rgba(240, 240, 240, 0.3)"
            onNodeClick={onMiniMapNodeClick}
          />
        </ReactFlow>
      </div>
      <div 
        className={`sidebar-wrapper${sidebarVisible ? '' : ' sidebar-hidden'}`}
        onMouseEnter={cancelHideTimer}
        onMouseLeave={startHideTimer}
      >
        <button
          className="sidebar-toggle-tab"
          onClick={() => setSidebarVisible(v => !v)}
          title={sidebarVisible ? t('org.hide_panel') : t('org.show_panel')}
        >
          <Iconify 
            icon={sidebarVisible ? 'eva:chevron-right-fill' : 'eva:chevron-left-fill'} 
            width={18} 
            height={18} 
          />
        </button>
        <div className="sidebar">

        
        <CollapsibleSection 
          title={t('org.tree_title')} 
          isOpen={openSection === 'tree'} 
          onToggle={() => setOpenSection(openSection === 'tree' ? null : 'tree')}
        >
          <div style={{ maxHeight: '55vh', overflowY: 'auto', paddingRight: '4px', paddingBottom: '10px' }}>
            {data?.orgContactGroups && <GroupTree groups={data.orgContactGroups} onRemoveUser={handleToggleGroup} onDeleteGroup={handleDeleteGroup} />}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title={t('org.dept_mgmt')} id="department-section" isOpen={openSection === 'dept'} onToggle={() => setOpenSection(openSection === 'dept' ? null : 'dept')}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <button onClick={handleAdd} className="primary-btn">{t('org.add_dept')}</button>
            <input 
              type="text" 
              placeholder={t('org.search_dept')} 
              value={searchGroupTerm}
              onChange={(e) => setSearchGroupTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span className="text-sub" style={{ fontSize: '0.9rem' }}>{t('org.total_depts')}: {filteredGroups.length}</span>
            {filteredGroups.length > itemsPerPage && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <button 
                  disabled={currentGroupPage === 1} 
                  onClick={() => setCurrentGroupPage(p => p - 1)}
                  className="pagination-btn"
                >
                  ◀
                </button>
                <span className="text-sub" style={{ fontSize: '0.9rem' }}>{currentGroupPage} / {totalGroupPages}</span>
                <button 
                  disabled={currentGroupPage === totalGroupPages} 
                  onClick={() => setCurrentGroupPage(p => p + 1)}
                  className="pagination-btn"
                >
                  ▶
                </button>
              </div>
            )}
          </div>
          <div style={{ maxHeight: '55vh', overflowY: 'auto', paddingRight: '4px', paddingBottom: '100px' }}>
          {displayedGroups.map((g, index) => (
            <div key={g.id} id={`group-section-${g.id}`} className={highlightedId === g.id ? 'group-row highlighted' : (index % 2 === 0 ? 'group-row list-row-even' : 'group-row list-row-odd')} style={{ alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, marginRight: 8 }}>
                <span style={{ cursor: 'pointer', wordBreak: 'break-word', lineHeight: '1.4', marginBottom: 4 }} onClick={() => handleRenameGroup(g.id, g.name)} title={t('org.rename_dept_prompt')}>
                  {g.name} ✎
                </span>
                <select 
                  value={g.parentId || ""} 
                  onChange={(e) => handleUpdateParent(g.id, e.target.value)}
                  style={{ width: '100%', maxWidth: '200px' }}
                  className="select-dropdown"
                >
                  <option value="">{t('org.no_parent')}</option>
                  {data?.orgContactGroups?.filter(pg => pg.id !== g.id).map(pg => (
                    <option key={pg.id} value={pg.id}>{pg.name}</option>
                  ))}
                </select>
              </div>
              <span 
                onClick={() => handleDeleteGroup(g.id)} 
                style={{ cursor: 'pointer', color: '#ff4d4f', display: 'flex', alignItems: 'center' }} 
                title={t('common.delete')}
              >
                <Iconify icon="eva:trash-2-outline" width={16} height={16} />
              </span>
            </div>
          ))}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title={t('org.people_mgmt')} isOpen={openSection === 'people'} onToggle={() => setOpenSection(openSection === 'people' ? null : 'people')}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <input 
              type="text" 
              placeholder={t('org.search_people')} 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span className="text-sub" style={{ fontSize: '0.9rem' }}>{t('org.total_people')}: {filteredUsers.length}</span>
            {filteredUsers.length > itemsPerPage && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <button 
                  disabled={currentUserPage === 1} 
                  onClick={() => setCurrentUserPage(p => p - 1)}
                  className="pagination-btn"
                >
                  ◀
                </button>
                <span className="text-sub" style={{ fontSize: '0.9rem' }}>{currentUserPage} / {totalUserPages}</span>
                <button 
                  disabled={currentUserPage === totalUserPages} 
                  onClick={() => setCurrentUserPage(p => p + 1)}
                  className="pagination-btn"
                >
                  ▶
                </button>
              </div>
            )}
          </div>
          
          <div style={{ maxHeight: '55vh', overflowY: 'auto', paddingRight: '4px', paddingBottom: '100px' }}>
          {displayedUsers.map((u, index) => (
            <div key={u.id} className={index % 2 === 0 ? 'user-row list-row-even' : 'user-row list-row-odd'}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{u.name}</div>
              
              {/* 已加入的群組標籤 */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                {u.groups?.map(g => (
                  <span key={g.id} className="user-tag">
                    {g.name}
                    <span onClick={() => handleToggleGroup(u.id, g.id)} className="remove-btn" title={t('org.remove_confirm')}>×</span>
                  </span>
                ))}
              </div>
              
              {/* 加入新群組選單 (過濾掉已加入的) */}
              <select style={{ width: '100%' }} className="select-dropdown" value="" onChange={(e) => handleToggleGroup(u.id, e.target.value)}>
                <option value="" disabled>{t('org.add_to_group')}</option>
                {data?.orgContactGroups?.filter(g => !u.groups?.some(ug => ug.id === g.id)).map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
          ))}
          </div>
        </CollapsibleSection>
          </div>
        </div>
      </div>
      </Container>
    </Page>
  );
};

const LayoutFlow = () => (
  <ReactFlowProvider>
    <OrgContactFlow />
  </ReactFlowProvider>
);

export default LayoutFlow;
